import { useEffect, useRef, type KeyboardEvent } from 'react';
import { Loader2, Send } from 'lucide-react';
import { Button } from '../ui/Button';
import { MarkdownPreview } from '../ui/MarkdownPreview';
import { cn } from '../../lib/utils';

export interface DraftAiSideChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface DraftAiSideChatProps {
  messages: DraftAiSideChatMessage[];
  input: string;
  sending: boolean;
  disabled?: boolean;
  statusLine?: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
}

export function DraftAiSideChat({
  messages,
  input,
  sending,
  disabled,
  statusLine,
  onInputChange,
  onSend,
}: DraftAiSideChatProps) {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onSend();
    }
  };

  return (
    <div className="flex h-full min-h-0 w-[22rem] shrink-0 flex-col overflow-hidden border-l border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900 xl:w-[26rem]">
      <div className="shrink-0 border-b border-gray-200 px-3 py-2 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">AI assistant</h3>
        <p className="text-[11px] text-gray-500 dark:text-gray-400">Ask for edits. The document on the left updates when the agent replies.</p>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {messages.map((msg, i) => (
          <div key={i} className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
            <div
              className={cn(
                'max-w-[95%] rounded-lg px-3 py-2 text-xs',
                msg.role === 'user'
                  ? 'bg-violet-600 text-white'
                  : 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100',
              )}
            >
              {msg.role === 'assistant' ? (
                <MarkdownPreview content={msg.content} variant="chat" className="text-xs" />
              ) : (
                <p className="whitespace-pre-wrap">{msg.content}</p>
              )}
            </div>
          </div>
        ))}
        {sending ? (
          <div className="flex items-center gap-2 text-[11px] text-gray-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {statusLine || 'Working…'}
          </div>
        ) : null}
        <div ref={endRef} />
      </div>
      <div className="shrink-0 border-t border-gray-200 p-2 dark:border-gray-700">
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(event) => onInputChange(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask for a change…"
            rows={3}
            disabled={sending || disabled}
            className="flex-1 resize-none rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
          <Button
            type="button"
            onClick={onSend}
            disabled={!input.trim() || sending || disabled}
            className="self-end"
            aria-label="Send"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
